import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LeadsTable } from '../components/crm/LeadsTable';
import { LeadFormModal } from '../components/crm/LeadFormModal';
import {
  getLeads,
  getTags,
  createLead,
  createTag,
  type LeadsFilter,
} from '../services/leadService';
import type { LeadWithTags, LeadTag, LeadStatus, LeadType, CreateLeadInput, UpdateLeadInput } from '../types/crm';
import { logger } from '../lib/logger';

const PAGE_SIZE = 20;

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState<LeadWithTags[]>([]);
  const [tags, setTags] = useState<LeadTag[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'ALL'>('ALL');
  const [typeFilter, setTypeFilter] = useState<LeadType | 'ALL'>('ALL');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const loadLeads = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const filters: LeadsFilter = {
        search: search || undefined,
        status: statusFilter,
        leadType: typeFilter,
      };
      const result = await getLeads(user.id, filters, {
        page: currentPage,
        pageSize: PAGE_SIZE,
      });
      setLeads(result.leads);
      setTotalCount(result.totalCount);
    } catch (error) {
      logger.error('Failed to load leads:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, search, statusFilter, typeFilter, currentPage]);

  const loadTags = useCallback(async () => {
    if (!user) return;
    try {
      const result = await getTags(user.id);
      setTags(result);
    } catch (error) {
      logger.error('Failed to load tags:', error);
    }
  }, [user]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, typeFilter]);

  const handleCreateLead = async (data: CreateLeadInput | UpdateLeadInput) => {
    if (!user) return;
    await createLead(user.id, data as CreateLeadInput);
    await loadLeads();
  };

  const handleCreateTag = async (name: string): Promise<LeadTag> => {
    if (!user) throw new Error('Not authenticated');
    const newTag = await createTag(user.id, name);
    setTags((prev) => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
    return newTag;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Leads</h1>
        <button
          data-tour="add-lead"
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Lead hinzufügen
        </button>
      </div>

      <div data-tour="lead-filters" className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nach Name oder E-Mail suchen..."
            className="w-full pl-12 pr-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as LeadType | 'ALL')}
            className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white w-full sm:w-auto sm:min-w-[130px]"
          >
            <option value="ALL">Alle Typen</option>
            <option value="CUSTOMER">Kunde</option>
            <option value="PARTNER">Partner</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeadStatus | 'ALL')}
            className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white w-full sm:w-auto sm:min-w-[150px]"
          >
            <option value="ALL">Alle Status</option>
            <option value="NEW">Neu</option>
            <option value="CONTACTED">Kontaktiert</option>
            <option value="IN_DISCUSSION">In Gespräch</option>
            <option value="WON">Gewonnen</option>
            <option value="LOST">Verloren</option>
          </select>
        </div>
      </div>

      {!isLoading && leads.length === 0 && !search && statusFilter === 'ALL' && typeFilter === 'ALL' ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Noch keine Leads vorhanden
          </h3>
          <p className="text-gray-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
            Erstelle deinen ersten Lead, um mit dem Tracking zu beginnen.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Ersten Lead erstellen
          </button>
        </div>
      ) : !isLoading && leads.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-12 text-center">
          <p className="text-gray-500 dark:text-slate-400">Keine Leads gefunden</p>
        </div>
      ) : (
        <>
          <div data-tour="lead-table">
            <LeadsTable leads={leads} isLoading={isLoading} />
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
              <p className="text-sm text-gray-500 text-center sm:text-left">
                {totalCount} Lead{totalCount !== 1 ? 's' : ''} insgesamt
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 px-2 sm:px-3 whitespace-nowrap">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <LeadFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateLead}
        tags={tags}
        onCreateTag={handleCreateTag}
      />
    </div>
  );
}
